import { Navigate } from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import { canAccessAdminPanel } from '@dcp/shared'
import { useProfile } from '@/hooks/useProfile'

/**
 * Props for the RequireAdminPanel gate component.
 *
 * @property children - Content to render when the system role is allowed.
 * @property fallback - Optional override for the denied state. Defaults to a
 *   redirect to `/settings/profile` — the only authenticated route that is
 *   never permission-gated, so a product Admin (system role `user`) cannot be
 *   trapped in a redirect loop.
 */
interface RequireAdminPanelProps {
  children: React.ReactNode
  fallback?: React.ReactNode
}

/**
 * Route-level guard for the `/admin/*` section. Mirrors `RequirePermission`
 * but checks the orthogonal system `user_role` via `canAccessAdminPanel`,
 * not the per-org `product_role`. The Admin section exposes cross-tenant
 * data (invitations, organizations) and must remain reachable only to
 * platform staff (`super_admin`, `admin`) — a product `admin` is org-scoped
 * and gets `manage_users` instead.
 *
 * Three states:
 * - Loading: spinner while the profile query is in flight (matches the
 *   ProtectedRoute pattern used by `App.tsx`).
 * - Denied: render `fallback`, defaulting to a redirect to
 *   `/settings/profile` (always reachable for any authenticated user).
 * - Allowed: render children as-is.
 *
 * Usage — wrap inside `<ProtectedRoute>` so the auth check runs first:
 * ```tsx
 * <ProtectedRoute>
 *   <RequireAdminPanel>
 *     <Outlet />
 *   </RequireAdminPanel>
 * </ProtectedRoute>
 * ```
 */
export function RequireAdminPanel({ children, fallback }: RequireAdminPanelProps) {
  const { data: profile, isLoading } = useProfile()

  // Spinner while the profile query is in flight to avoid a false-deny flash
  // before the system role is known.
  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    )
  }

  if (!profile?.role || !canAccessAdminPanel(profile.role)) {
    if (fallback !== undefined) {
      return <>{fallback}</>
    }
    return <Navigate to="/settings/profile" replace />
  }

  return <>{children}</>
}
